import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useAuth } from '../../hooks/useAuth';
import { usersService} from '../../services/users';
import type { User, UserRole } from '../../types/auth';

interface AdminPanelProps {
  show: boolean;
  onClose: () => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ show, onClose }) => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editForm, setEditForm] = useState({
    username: '',
    role: '' as UserRole,
    isActive: true
  });
  const [createForm, setCreateForm] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    role: '' as UserRole,
    department: ''
  });

  // Get available roles for creating/editing based on current user's permissions
  const getAvailableRoles = (): UserRole[] => {
    if (!user) return [];
    
    if (user.role === 'super admin') {
      return [
        'super admin',
        'Purchase admin',
        'Purchase supervisor', 
        'Purchase agent',
        'Supplies admin',
        'Supplies agent'
      ];
    } else if (user.role === 'Purchase admin' || user.role === 'Purchase supervisor') {
      return ['Purchase admin', 'Purchase supervisor', 'Purchase agent'];
    } else if (user.role === 'Supplies admin') {
      return ['Supplies admin', 'Supplies agent'];
    }
    
    return [];
  };

  // Get department from role
  const getDepartmentFromRole = (role: UserRole): string => {
    if (role === 'super admin') return 'Tay';
    if (role.includes('Purchase')) return 'Purchase';
    if (role.includes('Supplies')) return 'Supplies';
    return 'Tay';
  };

  useEffect(() => {
    if (show) {
      loadUsers();
    }
  }, [show]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await usersService.getUsers();
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error loading users:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to load users',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (userToEdit: User) => {
    setEditingUser(userToEdit);
    setEditForm({
      username: userToEdit.username,
      role: userToEdit.role,
      isActive: userToEdit.is_active || true
    });
  };

  const cancelEditing = () => {
    setEditingUser(null);
    setEditForm({
      username: '',
      role: '' as UserRole,
      isActive: true
    });
  };

  const saveUser = async () => {
    if (!editingUser) return;

    try {
      await usersService.updateUser(editingUser.id, {
        username: editForm.username,
        role: editForm.role,
        is_active: editForm.isActive
      });
      
      Swal.fire({
        title: 'Success!',
        text: 'User updated successfully',
        icon: 'success'
      });
      
      cancelEditing();
      await loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to update user',
        icon: 'error'
      });
    }
  };

  const toggleUserStatus = async (userId: number) => {
    try {
      await usersService.toggleUserStatus(userId);
      Swal.fire({
        title: 'Success!',
        text: 'User status updated successfully',
        icon: 'success'
      });
      await loadUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to update user status',
        icon: 'error'
      });
    }
  };

  const resetPassword = async (userId: number, username: string) => {
    const { value: password } = await Swal.fire({
      title: `Reset password for ${username}`,
      input: 'password',
      inputPlaceholder: 'Enter new password',
      inputAttributes: {
        autocapitalize: 'off'
      },
      showCancelButton: true,
      confirmButtonText: 'Reset Password',
      inputValidator: (value) => {
        if (!value) {
          return 'Password is required';
        }
        if (value.length < 6) {
          return 'Password must be at least 6 characters long';
        }
        return null;
      }
    });

    if (password) {
      try {
        await usersService.resetUserPassword(userId, password);
        Swal.fire({
          title: 'Success!',
          text: 'Password reset successfully',
          icon: 'success'
        });
      } catch (error) {
        console.error('Error resetting password:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to reset password',
          icon: 'error'
        });
      }
    }
  };

  // Create user functions
  const openCreateModal = () => {
    setCreateForm({
      username: '',
      password: '',
      confirmPassword: '',
      role: getAvailableRoles()[0] || '' as UserRole,
      department: ''
    });
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateForm({
      username: '',
      password: '',
      confirmPassword: '',
      role: '' as UserRole,
      department: ''
    });
  };

  const createUser = async () => {
    // Validate form
    if (!createForm.username.trim()) {
      Swal.fire({
        title: 'Error!',
        text: 'Username is required',
        icon: 'error'
      });
      return;
    }

    if (!createForm.password) {
      Swal.fire({
        title: 'Error!',
        text: 'Password is required',
        icon: 'error'
      });
      return;
    }

    if (createForm.password.length < 6) {
      Swal.fire({
        title: 'Error!',
        text: 'Password must be at least 6 characters long',
        icon: 'error'
      });
      return;
    }

    if (createForm.password !== createForm.confirmPassword) {
      Swal.fire({
        title: 'Error!',
        text: 'Passwords do not match',
        icon: 'error'
      });
      return;
    }

    if (!createForm.role) {
      Swal.fire({
        title: 'Error!',
        text: 'Role is required',
        icon: 'error'
      });
      return;
    }

    try {
      await usersService.createUser({
        username: createForm.username.trim(),
        password: createForm.password,
        role: createForm.role,
        department: getDepartmentFromRole(createForm.role)
      });

      Swal.fire({
        title: 'Success!',
        text: 'User created successfully',
        icon: 'success'
      });

      closeCreateModal();
      await loadUsers();
    } catch (error: any) {
      console.error('Error creating user:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create user';
      Swal.fire({
        title: 'Error!',
        text: errorMessage,
        icon: 'error'
      });
    }
  };

  // Check if current user can create users
  const canCreateUsers = () => {
    if (!user) return false;
    return user.role === 'super admin' || 
           user.role === 'Purchase admin' || 
           user.role === 'Supplies admin';
  };

  const getDepartmentDisplayName = (department: string) => {
    switch (department) {
      case 'Tay':
        return 'Tay';
      case 'Purchase':
        return 'Purchase';
      case 'Supplies':
        return 'Supplies';
      default:
        return department;
    }
  };

  const getDepartmentBadgeClass = (department: string) => {
    switch (department) {
      case 'Tay':
        return 'bg-warning text-dark';
      case 'Purchase':
        return 'bg-success text-white';
      case 'Supplies':
        return 'bg-info text-white';
      default:
        return 'bg-secondary text-white';
    }
  };

  const getRoleBadgeClass = (role: UserRole) => {
    switch (role) {
      case 'super admin':
        return 'bg-danger';
      case 'Purchase admin':
      case 'Supplies admin':
        return 'bg-primary';
      case 'Purchase supervisor':
        return 'bg-info';
      case 'Purchase agent':
      case 'Supplies agent':
        return 'bg-secondary';
      default:
        return 'bg-light text-dark';
    }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex={-1}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-people me-2"></i>
              User Management
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">User Management</h6>
                  {canCreateUsers() && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={openCreateModal}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Create User
                    </button>
                  )}
                </div>
                <div className="admin-panel-table-container">
                  <table className="admin-panel-table table table-hover">
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Role</th>
                        <th>Department</th>
                        <th>Status</th>
                        <th>Last Login</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                  <tbody>
                    {users.map((userItem) => (
                      <tr key={userItem.id}>
                        <td>
                          {editingUser?.id === userItem.id ? (
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={editForm.username}
                              onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value }))}
                            />
                          ) : (
                            userItem.username
                          )}
                        </td>
                        <td>
                          {editingUser?.id === userItem.id ? (
                            <select
                              className="form-select form-select-sm"
                              value={editForm.role}
                              onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value as UserRole }))}
                            >
                              {getAvailableRoles().map(role => (
                                <option key={role} value={role}>{role}</option>
                              ))}
                            </select>
                          ) : (
                            <span className={`badge ${getRoleBadgeClass(userItem.role)}`}>
                              {userItem.role}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${getDepartmentBadgeClass(userItem.department)}`}>
                            {getDepartmentDisplayName(userItem.department)}
                          </span>
                        </td>
                        <td>
                          {editingUser?.id === userItem.id ? (
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={editForm.isActive}
                                onChange={(e) => setEditForm(prev => ({ ...prev, isActive: e.target.checked }))}
                              />
                            </div>
                          ) : (
                            <span className={`badge ${userItem.is_active ? 'bg-success' : 'bg-danger'}`}>
                              {userItem.is_active ? 'Active' : 'Inactive'}
                            </span>
                          )}
                        </td>
                        <td>
                          {userItem.last_login ? 
                            new Date(userItem.last_login).toLocaleString('en-GB', {
                              day: '2-digit',
                              month: '2-digit', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            }).replace(',', '') : 'Never'}
                        </td>
                        <td>
                          {new Date(userItem.created_at).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </td>
                        <td>
                          {editingUser?.id === userItem.id ? (
                            <div className="btn-group btn-group-sm">
                              <button
                                className="btn btn-success btn-sm"
                                onClick={saveUser}
                              >
                                <i className="bi bi-check"></i>
                              </button>
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={cancelEditing}
                              >
                                <i className="bi bi-x"></i>
                              </button>
                            </div>
                          ) : (
                            <div className="btn-group btn-group-sm">
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => startEditing(userItem)}
                                title="Edit user"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                className="btn btn-outline-warning btn-sm"
                                onClick={() => resetPassword(userItem.id, userItem.username)}
                                title="Reset password"
                              >
                                <i className="bi bi-key"></i>
                              </button>
                              <button
                                className={`btn btn-outline-${userItem.is_active ? 'danger' : 'success'} btn-sm`}
                                onClick={() => toggleUserStatus(userItem.id)}
                                title={userItem.is_active ? 'Deactivate user' : 'Activate user'}
                              >
                                <i className={`bi ${userItem.is_active ? 'bi-person-x' : 'bi-person-check'}`}></i>
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <>
          <div className="modal show d-block" tabIndex={-1}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-person-plus me-2"></i>
                    Create New User
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeCreateModal}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <form>
                    <div className="mb-3">
                      <label className="form-label">Username</label>
                      <input
                        type="text"
                        className="form-control"
                        value={createForm.username}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, username: e.target.value }))}
                        placeholder="Enter username"
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Role</label>
                      <select
                        className="form-select"
                        value={createForm.role}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, role: e.target.value as UserRole }))}
                      >
                        <option value="">Select Role</option>
                        {getAvailableRoles().map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={createForm.password}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Enter password (min 6 characters)"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Confirm Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={createForm.confirmPassword}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        placeholder="Confirm password"
                      />
                    </div>
                  </form>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeCreateModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={createUser}
                  >
                    <i className="bi bi-plus-circle me-1"></i>
                    Create User
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </>
      )}
    </div>
  );
};

export default AdminPanel;
